var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { uuid } from "../../Common";
import { joinedConferenceStateSelector, joinedConferenceSimpleViewSelector, joinedConferenceSelector, joinedConferenceMetadataByKeySelector, endpointMetadataSelector, joinedConferenceMetadataSelector, } from "./selectors";
import { JoinedConferenceState, ConferenceEventType, EndpointState, } from "./model";
import { leaveConferenceRequest, endConferenceRequest, conferenceHasEnded, conferenceHoldEvent, trackMuteUpdateEvent, } from "./actions";
import { stopViewStreamRequest, viewStreamRequest } from "./view/actions";
import { publishStreamRequest, unpublishStreamRequest, } from "./publish/actions";
import { updateConferenceMetadata, postStatsNewSession } from "./sagas";
import { log, eventLog } from "../../log";
export class ConferenceSessionImpl {
    constructor(context, options) {
        this.onHold = false;
        this.id = uuid();
        this.parentContext = context;
        this.eventHandlers = options.eventHandlers ? options.eventHandlers : {};
        this.conferenceId = options.id;
        this.publishTasks = {};
        this.statisticsOptions = options.statistics;
        this.viewStreamMetadataProvider = options.viewStreamMetadataProvider;
    }
    get conference() {
        return joinedConferenceSimpleViewSelector(this.parentContext.getStore().getState(), this.conferenceId);
    }
    get direction() {
        return "outgoing";
    }
    get remoteEndpoint() {
        return {
            username: this.conference.name,
            endpoint: `conference://${this.conference.name}`,
        };
    }
    // IConferenceSession methods start
    isInProgress() {
        return this.compareJoinedConferenceStateTo(JoinedConferenceState.JOINING);
    }
    isEstablished() {
        return this.compareJoinedConferenceStateTo(JoinedConferenceState.JOINED);
    }
    isClosing() {
        return (!this.conference ||
            this.compareJoinedConferenceStateTo(JoinedConferenceState.LEAVING) ||
            this.compareJoinedConferenceStateTo(JoinedConferenceState.ENDING));
    }
    syncHoldState() {
        const metadata = joinedConferenceMetadataByKeySelector(this.getParentContext().getStore().getState(), this.conference.name, "ON_HOLD");
        const change = metadata ? JSON.parse(metadata[0].value) : false;
        if (this.setHoldState(change)) {
            this.sendEvent("holdUpdated", Object.assign({ enabled: this.isOnHold() }, (metadata ? metadata[0] : {})));
        }
    }
    /**
     * Changes local state and dispatches action if so
     * @return If state was changed
     */
    setHoldState(enabled) {
        if (this.isOnHold() === enabled) {
            return false;
        }
        this.onHold = enabled;
        // event that actually changes state
        this.parentContext.getStore().dispatch(conferenceHoldEvent({
            id: this.conference.name,
            enabled,
        }));
        return true;
    }
    isOnHold() {
        return this.onHold === true;
    }
    hold() {
        return __awaiter(this, void 0, void 0, function* () {
            const task = this.parentContext.runSaga(updateConferenceMetadata, this, "SET", "ON_HOLD", true);
            yield task.toPromise();
        });
    }
    unhold() {
        return __awaiter(this, void 0, void 0, function* () {
            const task = this.parentContext.runSaga(updateConferenceMetadata, this, "REMOVE", "ON_HOLD", false);
            yield task.toPromise();
        });
    }
    publish(options) {
        return new Promise((resolve, reject) => {
            const streamId = uuid();
            this.publishTasks[streamId] = {
                options,
                promise: { resolve, reject },
                manager: null,
            };
            this.parentContext.getStore().dispatch(publishStreamRequest({
                conferenceId: this.conference.name,
                streamId,
            }));
        });
    }
    unpublish(stream) {
        this.parentContext
            .getStore()
            .dispatch(unpublishStreamRequest({ streamId: stream.id }));
    }
    leave(reason) {
        this.parentContext
            .getStore()
            .dispatch(leaveConferenceRequest({ name: this.conference.name, reason }));
    }
    terminate(reason) {
        this.parentContext
            .getStore()
            .dispatch(endConferenceRequest({ name: this.conference.name, reason }));
    }
    on(eventId, handler) {
        this.setEventHandler(eventId, handler);
    }
    getViewStreamMetadataProvider() {
        return this.viewStreamMetadataProvider;
    }
    getConferenceMetadata() {
        return joinedConferenceMetadataSelector(this.parentContext.getStore().getState(), this.conferenceId);
    }
    setConferenceMetadata(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            const resource = this.parentContext.getConferenceResource();
            yield resource.create({
                conferenceId: this.conferenceId,
                userEndpointId: this.parentContext.endpoint(),
                operation: "SET",
                key,
                value,
            }, {
                urlPostfix: "updateMetadata",
            });
        });
    }
    removeConferenceMetadata(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const resource = this.parentContext.getConferenceResource();
            yield resource.create({
                conferenceId: this.conferenceId,
                userEndpointId: this.parentContext.endpoint(),
                operation: "REMOVE",
                key,
            }, {
                urlPostfix: "updateMetadata",
            });
        });
    }
    updateMetadata(newMetadata) {
        return __awaiter(this, void 0, void 0, function* () {
            const resource = this.parentContext.getConferenceResource();
            yield resource.create({
                conferenceId: this.conferenceId,
                newMetadata,
                userEndpointId: this.parentContext.endpoint(),
            }, {
                urlPostfix: "updateParticipantMetadata",
            });
        });
    }
    getMetadata(endpoint) {
        return endpointMetadataSelector(this.getParentContext().getStore().getState(), this.conferenceId, endpoint.username, endpoint.endpoint);
    }
    getConferenceResource() {
        return this.parentContext.getConferenceResource();
    }
    getParentContext() {
        return this.parentContext;
    }
    sendEvent(eventId, eventData) {
        eventLog.debug(`conference session event ${eventId}`, eventData);
        if (this.hasEventHandler(eventId)) {
            try {
                if (eventData) {
                    this.getEventHandler(eventId)(eventData);
                }
                else {
                    this.getEventHandler(eventId)();
                }
                /* temp debug code, check if mediastreams only enabled tracks
                        if (eventData && eventData.mediaStream) {
                            const audioTracks = eventData.mediaStream.getAudioTracks();
                            const videoTracks = eventData.mediaStream.getVideoTracks();
        
                            const audioEnabled = audioTracks.filter((t) => t.enabled);
                            const videoEnabled = videoTracks.filter((t) => t.enabled);
        
                            console.log("MediaStreamio", eventId, eventData);
                            console.log("MediaStreamio",
                                `${audioTracks.length} audios, ${audioEnabled.length} enabled`);
                            console.log("MediaStreamio",
                                `${videoTracks.length} videos, ${videoEnabled.length} enabled`);
                        }
                        */
            }
            catch (error) {
                log.error(`error while executing conference session
                 ${eventId} handler`, error);
            }
        }
    }
    processInitialState() {
        const conferenceId = this.conference.name;
        const userEndpointId = this.parentContext.endpoint();
        const joinedConference = joinedConferenceSelector(this.parentContext.getStore().getState(), conferenceId);
        for (const participant of joinedConference.conferenceState.participants) {
            for (const endpoint of participant.endpoints) {
                if (endpoint.id === userEndpointId) {
                    continue;
                }
                // send endpointJoinedEvent
                const endpointEvent = {
                    username: participant.id,
                    endpoint: endpoint.id,
                    metadata: endpoint.metadata,
                };
                this.sendEvent("endpointJoined", endpointEvent);
                if (endpoint.state === EndpointState.SICK) {
                    this.sendEvent("endpointGotSick", endpointEvent);
                }
                for (const stream of endpoint.streams) {
                    const payload = {
                        conferenceId,
                        userId: participant.id,
                        userEndpointId: endpoint.id,
                        streamId: stream.id,
                        streamType: stream.type.toLowerCase(),
                    };
                    this.parentContext.getStore().dispatch(viewStreamRequest(payload));
                }
            }
        }
        // sync hold state
        this.syncHoldState();
        // send conference metadata SET events for any key
        for (const mKey of Object.keys(joinedConference.conferenceState.metadata)) {
            const entry = joinedConference.conferenceState.metadata[mKey];
            const event = {
                operation: "SET",
                key: mKey,
                value: entry.value,
                userId: entry.userId,
                userEndpointId: entry.userEndpointId,
            };
            this.sendEvent("conferenceMetadataSet", event);
        }
        // notify stats backend of possibly new conference
        this.notifyStatsSession(joinedConference.conferenceState.participants);
    }
    processEvent(event) {
        const userEndpointId = this.parentContext.endpoint();
        switch (event.type) {
            case ConferenceEventType.CONFERENCE_JOINED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const endpoint = {
                    username: event.userId,
                    endpoint: event.userEndpointId,
                    metadata: event.metadata,
                };
                this.sendEvent("endpointJoined", endpoint);
                break;
            }
            case ConferenceEventType.CONFERENCE_PARTICIPANT_STATE_CHANGED_EVENT: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const endpoint = {
                    username: event.userId,
                    endpoint: event.userEndpointId,
                };
                if (event.state === EndpointState.SICK) {
                    this.sendEvent("endpointGotSick", endpoint);
                }
                else if (event.state === EndpointState.HEALTHY) {
                    this.sendEvent("endpointRecovered", endpoint);
                }
                break;
            }
            case ConferenceEventType.CONFERENCE_LEFT: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const endpoint = {
                    username: event.userId,
                    endpoint: event.userEndpointId,
                    metadata: {
                        reason: event.reason,
                    },
                };
                this.sendEvent("endpointLeft", endpoint);
                break;
            }
            case ConferenceEventType.CONFERENCE_ENDED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const endpoint = {
                    username: event.userId,
                    endpoint: event.userEndpointId,
                    metadata: {
                        reason: event.reason,
                    },
                };
                this.sendEvent("endpointLeft", endpoint);
                this.getParentContext()
                    .getStore()
                    .dispatch(conferenceHasEnded({
                    conferenceId: this.conference.name,
                    reason: event.reason,
                }));
                break;
            }
            case ConferenceEventType.CONFERENCE_STREAM_PUBLISHED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const payload = {
                    conferenceId: event.conferenceId,
                    userId: event.userId,
                    userEndpointId: event.userEndpointId,
                    streamId: event.streamId,
                    streamType: event.streamType.toLowerCase(),
                };
                this.parentContext.getStore().dispatch(viewStreamRequest(payload));
                break;
            }
            case ConferenceEventType.CONFERENCE_STREAM_UNPUBLISHED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                const payload = {
                    conferenceId: event.conferenceId,
                    userId: event.userId,
                    userEndpointId: event.userEndpointId,
                    streamId: event.streamId,
                };
                this.parentContext.getStore().dispatch(stopViewStreamRequest(payload));
                break;
            }
            case ConferenceEventType.CONFERENCE_METADATA_UPDATE: {
                if (event.key === "ON_HOLD") {
                    this.syncHoldState();
                }
                else if (event.key.includes("TRACK_MUTED")) {
                    const [action, kind, streamId] = event.key.split("/");
                    this.parentContext.getStore().dispatch(trackMuteUpdateEvent(Object.assign(Object.assign({}, event), { action,
                        kind,
                        streamId })));
                }
                switch (event.operation) {
                    case "SET":
                        this.sendEvent("conferenceMetadataSet", event);
                        break;
                    case "REMOVE":
                        this.sendEvent("conferenceMetadataRemoved", event);
                        break;
                }
                break;
            }
            case ConferenceEventType.CONFERENCE_NETWORK_INDICATOR: {
                this.sendEvent("networkQualityReport", event);
                break;
            }
            case ConferenceEventType.CONFERENCE_PARTICIPANT_METADATA_UPDATED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                this.sendEvent("endpointMetadataUpdated", {
                    userId: event.userId,
                    userEndpointId: event.userEndpointId,
                    newMetadata: event.newMetadata,
                });
                break;
            }
            case ConferenceEventType.CONFERENCE_STREAM_METADATA_UPDATED: {
                if (event.userEndpointId === userEndpointId) {
                    return;
                }
                this.sendEvent("streamMetadataUpdated", {
                    userId: event.userId,
                    userEndpointId: event.userEndpointId,
                    streamId: event.streamId,
                    newMetadata: event.newMetadata,
                });
            }
        }
    }
    // things I don't want to share outside this class
    // notify of new session if first in room
    notifyStatsSession(participants) {
        return __awaiter(this, void 0, void 0, function* () {
            if (participants.length > 1) {
                return;
            }
            const task = this.parentContext.runSaga(postStatsNewSession, this.getParentContext().getStatsResource(), {
                firstInRoom: true,
                sessionId: this.conference.name,
                type: "conference",
            });
            yield task.toPromise();
        });
    }
    hasEventHandler(eventId) {
        return !!this.eventHandlers[eventId];
    }
    getEventHandler(eventId) {
        return this.eventHandlers[eventId];
    }
    setEventHandler(eventId, handler) {
        this.eventHandlers[eventId] = handler;
    }
    compareJoinedConferenceStateTo(state) {
        const conference = this.conference;
        return (!!conference &&
            joinedConferenceStateSelector(this.parentContext.getStore().getState(), conference.name) === state);
    }
}
//# sourceMappingURL=ConferenceSessionImpl.js.map